/*
 * <recipe-filters> — instant, client-side filtering for the recipe grid.
 *
 * Filtering model:
 *   - Chips are grouped by data-filter-group.
 *   - Within a group, active chips are OR'd (match ANY selected value).
 *   - Across groups, results are AND'd (must match every active group).
 *   - A text search matches against each card's title.
 *   - No network requests / no page reloads — keeps people on the page.
 */
class RecipeFilters extends HTMLElement {
  constructor() {
    super();
    this.cards = Array.from(this.querySelectorAll('[data-recipe-card]'));
    this.chips = Array.from(this.querySelectorAll('[data-filter-value]'));
    this.searchInput = this.querySelector('[data-recipe-search]');
    this.countEl = this.querySelector('[data-recipe-count]');
    this.clearBtn = this.querySelector('[data-recipe-clear]');
    this.emptyEl = this.querySelector('[data-recipe-empty]');
    this.query = '';
  }

  connectedCallback() {
    this.chips.forEach((chip) => {
      chip.addEventListener('click', () => this.onChipClick(chip));
    });

    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => {
        this.query = this.searchInput.value.trim().toLowerCase();
        this.apply();
      });
    }

    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', () => this.clearAll());
    }

    this.apply();
  }

  onChipClick(chip) {
    const group = chip.dataset.filterGroup;
    const value = chip.dataset.filterValue;
    const groupChips = this.chips.filter((c) => c.dataset.filterGroup === group);

    if (value === '') {
      // "All" chip: reset this group.
      groupChips.forEach((c) => this.setPressed(c, c === chip));
    } else {
      // Toggle this chip; clear the group's "All" chip.
      this.setPressed(chip, chip.getAttribute('aria-pressed') !== 'true');
      const allChip = groupChips.find((c) => c.dataset.filterValue === '');
      const anyActive = groupChips.some(
        (c) => c.dataset.filterValue !== '' && c.getAttribute('aria-pressed') === 'true'
      );
      if (allChip) this.setPressed(allChip, !anyActive);
    }

    this.apply();
  }

  setPressed(chip, pressed) {
    chip.setAttribute('aria-pressed', pressed ? 'true' : 'false');
  }

  activeByGroup() {
    const map = {};
    this.chips.forEach((chip) => {
      const value = chip.dataset.filterValue;
      if (value === '') return;
      if (chip.getAttribute('aria-pressed') !== 'true') return;
      const group = chip.dataset.filterGroup;
      (map[group] = map[group] || []).push(value.toLowerCase());
    });
    return map;
  }

  apply() {
    const active = this.activeByGroup();
    const groups = Object.keys(active);
    let visible = 0;

    this.cards.forEach((card) => {
      const tags = (card.dataset.tags || '')
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
      const title = card.dataset.title || '';

      const matchesTags = groups.every((group) =>
        active[group].some((value) => tags.includes(value))
      );
      const matchesSearch = this.query === '' || title.indexOf(this.query) !== -1;
      const show = matchesTags && matchesSearch;

      card.hidden = !show;
      if (show) visible += 1;
    });

    this.updateMeta(visible, groups.length > 0 || this.query !== '');
  }

  updateMeta(visible, hasActiveFilters) {
    if (this.countEl) {
      const noun = visible === 1 ? 'recipe' : 'recipes';
      this.countEl.textContent = `Showing ${visible} ${noun}`;
    }
    if (this.clearBtn) {
      this.clearBtn.classList.toggle('is-visible', hasActiveFilters);
    }
    if (this.emptyEl) {
      this.emptyEl.classList.toggle('is-visible', visible === 0);
    }
  }

  clearAll() {
    this.chips.forEach((chip) => {
      this.setPressed(chip, chip.dataset.filterValue === '');
    });
    if (this.searchInput) this.searchInput.value = '';
    this.query = '';
    this.apply();
  }
}

if (!customElements.get('recipe-filters')) {
  customElements.define('recipe-filters', RecipeFilters);
}
